import { type ReactNode, useEffect, useState } from "react";
import {
	Button,
	Menu,
	MenuItem,
	MenuTrigger,
	Popover,
} from "react-aria-components";
import { cn, tv } from "tailwind-variants";
import type { AnimationScrollType } from "#/_packages/umbrella/types";
import { getLinkAttributes } from "#/_packages/umbrella/utils/link-attributes";

export type DropdownSize =
	| "extraSmall"
	| "small"
	| "medium"
	| "large"
	| "extraLarge";

export type DropdownIntent =
	| "primary"
	| "secondary"
	| "light"
	| "dark"
	| "white"
	| "black"
	| "success"
	| "warning"
	| "danger"
	| "info"
	| "none";

export type DropdownVariant = "solid" | "outline" | "ghost" | "soft" | "link";
export type DropdownDirection = "down" | "end" | "start" | "up";
export type DropdownIconPosition = "start" | "end";

export type DropdownItem = {
	trackingEvent?: string;
	noFollow?: boolean;
	id: string;
	name: string;
	url: string;
	target?: "_self" | "_blank" | "_parent" | "_top";
};

export type DropdownProps = {
	id?: string;
	textLabel?: string;
	icon?: ReactNode;
	iconPosition?: DropdownIconPosition;
	size?: DropdownSize;
	intentColor?: DropdownIntent;
	typeVariant?: DropdownVariant;
	direction?: DropdownDirection;
	dropdownItems: readonly DropdownItem[];
	animationScrollType?: AnimationScrollType | "";
	className?: string;
};

const dropdown = tv({
	slots: {
		root: "block__dropdown",
		direction: "relative inline-block",
		button: "btn inline-flex items-center gap-1 dropdown-toggle",
		menu: "dropdown-menu min-w-40",
		item: "dropdown-item block outline-none",
	},
	variants: {
		size: {
			extraSmall: { button: "btn-xs" },
			small: { button: "btn-sm" },
			medium: { button: "" },
			large: { button: "btn-lg" },
			extraLarge: { button: "btn-xl" },
		},
		intentColor: {
			primary: { button: "btn-primary" },
			secondary: { button: "btn-secondary" },
			light: { button: "btn-light" },
			dark: { button: "btn-dark" },
			white: { button: "btn-white" },
			black: { button: "btn-black" },
			success: { button: "btn-success" },
			warning: { button: "btn-warning" },
			danger: { button: "btn-danger" },
			info: { button: "btn-info" },
			none: { button: "btn-none" },
		},
		typeVariant: {
			solid: { button: "" },
			outline: { button: "btn-outline" },
			ghost: { button: "btn-ghost" },
			soft: { button: "btn-soft" },
			link: { button: "btn-link" },
		},
		direction: {
			down: { direction: "dropdown" },
			end: { direction: "dropend" },
			start: { direction: "dropstart" },
			up: { direction: "dropup" },
		},
		alignmentSelfVertical: {
			stretch: { root: "" },
			start: { root: "self-start" },
			center: { root: "self-center" },
			end: { root: "self-end" },
		},
	},
	defaultVariants: {
		size: "medium",
		intentColor: "primary",
		typeVariant: "solid",
		direction: "down",
		alignmentSelfVertical: "stretch",
	},
});

const popoverPlacement: Record<
	DropdownDirection,
	"bottom start" | "right top" | "left top" | "top start"
> = {
	down: "bottom start",
	end: "right top",
	start: "left top",
	up: "top start",
};

export function Dropdown({
	id,
	textLabel,
	icon,
	iconPosition = "start",
	size = "medium",
	intentColor = "primary",
	typeVariant = "solid",
	direction = "down",
	dropdownItems,
	animationScrollType,
	className,
}: DropdownProps) {
	const [portalContainer, setPortalContainer] = useState<HTMLElement>();

	useEffect(() => {
		// Umbrella's body is positioned and can be taller than the viewport.
		// Give React Aria a viewport-sized containing block for top/left placement.
		const container = document.createElement("div");
		Object.assign(container.style, {
			position: "fixed",
			inset: "0",
			pointerEvents: "none",
			zIndex: "100000",
		});
		document.body.append(container);
		setPortalContainer(container);
		return () => container.remove();
	}, []);

	const {
		root,
		direction: directionClass,
		button,
		menu,
		item,
	} = dropdown({
		size,
		intentColor,
		typeVariant,
		direction,
	});

	return (
		<div id={id} className={cn(root(), animationScrollType, className)}>
			<div className={directionClass()}>
				<MenuTrigger>
					<Button type="button" className={button()}>
						{iconPosition === "start" ? icon : null}
						{textLabel}
						{iconPosition === "end" ? icon : null}
					</Button>
					<Popover
						placement={popoverPlacement[direction]}
						offset={4}
						UNSTABLE_portalContainer={portalContainer}
						className="pointer-events-auto"
					>
						<Menu className={menu()}>
							{dropdownItems.map((dropdownItem) => (
								<MenuItem
									key={dropdownItem.id}
									href={dropdownItem.url}
									target={dropdownItem.target}
									{...getLinkAttributes(dropdownItem)}
									className={item()}
								>
									{dropdownItem.name}
								</MenuItem>
							))}
						</Menu>
					</Popover>
				</MenuTrigger>
			</div>
		</div>
	);
}
